import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import client from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Bell, AlertTriangle, XCircle, Info, RefreshCw, ListChecks, IdCard, FileCheck2, ShoppingCart, Inbox, ChevronRight, CheckCheck, X } from 'lucide-react';

const TYPE_META = {
  alert: { Icon: AlertTriangle, label: 'Alert' },
  todo: { Icon: ListChecks, label: 'To-Do' },
  incomplete_form: { Icon: IdCard, label: 'Incomplete Form' },
  approval: { Icon: FileCheck2, label: 'Approval' },
  order: { Icon: ShoppingCart, label: 'Order' },
};

const levelColor = (lvl) => lvl === 'danger' ? 'hsl(var(--danger))' : lvl === 'warning' ? 'hsl(var(--warning))' : 'hsl(var(--info))';

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const canScan = ['developer', 'company_admin', 'der', 'owner_operator'].includes(user?.role);

  const load = () => client.get('/inbox').then((r) => setData(r.data)).catch(() => setData({ items: [], counts: { all: 0 } }));
  useEffect(() => { load(); }, []);

  const scan = async () => {
    try { const r = await client.post('/notifications/scan?send_emails=true'); toast.success(`Scan complete — ${r.data.created} new alert(s)${r.data.emailed != null ? `, ${r.data.emailed} email(s) sent` : ''}`); load(); }
    catch { toast.error('Scan failed'); }
  };

  const dismissAll = async () => {
    try { const r = await client.post('/inbox/dismiss-all'); toast.success(`Cleared ${r.data.dismissed} item(s)`); load(); }
    catch { toast.error('Could not clear items'); }
  };

  const dismissOne = async (id) => {
    setData((p) => p ? { ...p, items: p.items.filter((i) => i.id !== id) } : p);
    try { await client.post('/inbox/dismiss', { ids: [id] }); }
    catch { toast.error('Could not dismiss'); load(); }
  };

  const categories = useMemo(() => {
    if (!data) return [];
    const c = data.counts || {};
    return ['all', ...Object.keys(c).filter((k) => k !== 'all')];
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return filter === 'all' ? data.items : data.items.filter((i) => i.category === filter);
  }, [data, filter]);

  return (
    <AppShell title="Notifications">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5" data-testid="inbox-filters">
          {categories.map((cat) => (
            <button key={cat} onClick={() => setFilter(cat)} data-testid={`inbox-filter-${cat.toLowerCase().replace(/[^a-z]+/g,'-')}`}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${filter === cat ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white' : 'bg-white text-muted-foreground hover:bg-muted'}`}>
              {cat === 'all' ? 'All' : cat}
              <span className={`rounded-full px-1.5 text-[10px] ${filter === cat ? 'bg-white/25' : 'bg-muted'}`}>{data?.counts?.[cat] ?? 0}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {(data?.counts?.all || 0) > 0 && <Button variant="outline" onClick={dismissAll} data-testid="inbox-mark-all-read"><CheckCheck className="mr-1.5 h-4 w-4" />Mark all read</Button>}
          {canScan && <Button variant="outline" onClick={scan} data-testid="notifications-scan-button"><RefreshCw className="mr-1.5 h-4 w-4" />Run Expiration Scan</Button>}
        </div>
      </div>

      {data === null ? <div className="space-y-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-16" />)}</div> : filtered.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 p-12 text-center"><Inbox className="h-8 w-8 text-muted-foreground" /><p className="text-sm text-muted-foreground">You're all caught up. Nothing here.</p></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((n) => {
            const meta = TYPE_META[n.type] || { Icon: Info, label: 'Item' };
            const Ic = meta.Icon;
            const color = levelColor(n.level);
            return (
              <Card key={n.id} className="flex items-center gap-3 p-4 transition-shadow hover:shadow-[0_8px_24px_rgba(15,23,42,0.06)]" data-testid={`inbox-item-${n.id}`}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: `color-mix(in srgb, ${color} 14%, transparent)` }}>
                  <Ic className="h-5 w-5" style={{ color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{n.title}</span>
                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{meta.label}</span>
                  </div>
                  <div className="truncate text-sm text-muted-foreground">{n.body}</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</div>
                </div>
                {n.link && <Button variant="ghost" size="sm" onClick={() => navigate(n.link)} data-testid={`inbox-open-${n.id}`}>Open<ChevronRight className="ml-1 h-4 w-4" /></Button>}
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => dismissOne(n.id)} data-testid={`inbox-dismiss-${n.id}`} aria-label="Dismiss"><X className="h-4 w-4" /></Button>
              </Card>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
